/**
 * Client-side layout findings for the canvas builder (audit R25; spec section
 * 13.4 rule table).
 *
 * PURPOSE AND SCOPE. The authoritative layout constraint engine lives on the
 * backend (spec section 13.4; canvas_layout_validator.py, Slice A). This module
 * is deliberately a STRICT SUBSET of that engine, used only to give the author a
 * live in-builder preview. It emits findings ONLY for the rules the frontend can
 * evaluate unambiguously from element geometry and top-level type, and it never
 * emits a finding the backend would not. The full subtype-keyed min-size table
 * (spec section 13.4a: CARD/TABS/CHART/TABLE/IMAGE floors resolved from config
 * discriminators) is backend-owned and intentionally NOT reproduced here.
 *
 * The one property this surface exists to demonstrate is the LAY004 contract:
 * an element below its minimum size is grown to the minimum AND the
 * normalization is REPORTED (never silently clamped). A `warn` finding with a
 * `normalization` descriptor carries the from -> to change so the display can
 * show it; a `error` finding blocks the save.
 *
 * NOTE (flagged to plan, not silently reconciled): the pre-existing FE
 * `ELEMENT_MIN_SIZES` table in types/index.ts gives `field` a minColumnSpan of 2,
 * which contradicts the frozen spec section 13.4a decision that FIELD is
 * deliberately floor-less. This module therefore does NOT consume that table; it
 * applies only the frozen-spec generic CONTAINER floor (2x4). Reconciling the FE
 * table against the frozen contract is a separate backend-aligned change.
 */

import { validateNoOverlaps } from "./canvasValidation";
import type { CanvasElementPosition } from "./canvasValidation";

/** The spec section 13.4 rule codes the frontend preview can evaluate. */
export type LayCode = "LAY001" | "LAY002" | "LAY003" | "LAY004";

export type LaySeverity = "error" | "warn";

/**
 * A single normalization the engine would apply (spec 13.4 LAY004). Present only
 * on `warn` findings that grow an element to a minimum; the display renders the
 * from -> to so the change is never silent.
 */
export interface LayNormalization {
  field: "columnSpan" | "rowSpan";
  from: number;
  to: number;
}

/** A layout finding in the shape the backend engine reports (subset of codes). */
export interface LayFinding {
  code: LayCode;
  severity: LaySeverity;
  elementId?: string;
  message: string;
  /** Present on LAY004 warns: the min-size growth that was applied and reported. */
  normalization?: LayNormalization;
}

/** An element as this evaluator needs it: geometry, id, and top-level type. */
export interface LayElementInput extends CanvasElementPosition {
  /** Top-level element type (frontend enum, lower-case). */
  type?: string;
  /** Nesting scope; child geometry is local to this parent/panel. */
  parentElementId?: string;
  parentPanelId?: string;
  /** Parent container's internal grid width, when this is a nested child. */
  parentGridColumns?: number;
}

/**
 * Generic minimum sizes taken from the FROZEN spec (section 13.4/13.4a), keyed
 * by top-level type only. FIELD is deliberately ABSENT (no field-width floor, by
 * choice). Only CONTAINER carries a confirmed generic floor (2x4); subtype
 * floors are backend-owned and not applied here. WIDGET/STATIC inherit the 1x1
 * default, i.e. no floor.
 */
const FROZEN_GENERIC_MIN_SIZES: Record<
  string,
  { minColumnSpan: number; minRowSpan: number }
> = {
  container: { minColumnSpan: 2, minRowSpan: 4 },
};

export interface EvaluateLayoutOptions {
  /** The canvas grid column count (spec grid G). Defaults to 12. */
  gridColumns?: number;
}

/**
 * Evaluate the frontend-checkable subset of the spec 13.4 rules over the given
 * elements. Returns findings in rule order; a canvas can carry several at once.
 *
 * Rule evaluation order mirrors the spec's one short-circuit: LAY002
 * (positive span) is a structural precondition, so when it fires for an element
 * that element is NOT also run through LAY004 (you cannot grow a non-positive
 * span to a minimum coherently). All other rules are independent.
 */
export function evaluateLayoutFindings(
  elements: LayElementInput[],
  options: EvaluateLayoutOptions = {},
): LayFinding[] {
  const gridColumns = options.gridColumns ?? 12;
  const findings: LayFinding[] = [];

  for (const element of elements) {
    const id = element.id;
    const nonPositiveSpan = element.columnSpan < 1 || element.rowSpan < 1;

    // LAY002 POSITIVE_SPAN (error, reject). Structural precondition.
    if (nonPositiveSpan) {
      findings.push({
        code: "LAY002",
        severity: "error",
        elementId: id,
        message: `Element span must be at least 1x1 (has ${element.columnSpan}x${element.rowSpan}).`,
      });
    }

    const elementGridColumns =
      element.parentElementId &&
      Number.isInteger(element.parentGridColumns) &&
      (element.parentGridColumns as number) > 0
        ? (element.parentGridColumns as number)
        : gridColumns;

    // LAY001 GRID_BOUNDS (error, reject): column start >= 1 and the element's
    // right edge stays within the grid.
    if (element.gridColumn < 1) {
      findings.push({
        code: "LAY001",
        severity: "error",
        elementId: id,
        message: `Element column start must be at least 1 (has ${element.gridColumn}).`,
      });
    } else if (
      !nonPositiveSpan &&
      element.gridColumn + element.columnSpan - 1 > elementGridColumns
    ) {
      findings.push({
        code: "LAY001",
        severity: "error",
        elementId: id,
        message: `Element extends past the ${elementGridColumns}-column grid (column ${element.gridColumn} + span ${element.columnSpan}).`,
      });
    }

    // LAY004 MIN_SIZE (warn, normalization). Skipped for a non-positive span
    // (LAY002 short-circuit): a malformed span is not a size to grow.
    if (!nonPositiveSpan) {
      const floor = element.type
        ? FROZEN_GENERIC_MIN_SIZES[element.type]
        : undefined;
      if (floor) {
        if (element.columnSpan < floor.minColumnSpan) {
          findings.push({
            code: "LAY004",
            severity: "warn",
            elementId: id,
            message: `Element is below the minimum width for its type; grown to ${floor.minColumnSpan} columns and reported (not silently applied).`,
            normalization: {
              field: "columnSpan",
              from: element.columnSpan,
              to: floor.minColumnSpan,
            },
          });
        }
        if (element.rowSpan < floor.minRowSpan) {
          findings.push({
            code: "LAY004",
            severity: "warn",
            elementId: id,
            message: `Element is below the minimum height for its type; grown to ${floor.minRowSpan} rows and reported (not silently applied).`,
            normalization: {
              field: "rowSpan",
              from: element.rowSpan,
              to: floor.minRowSpan,
            },
          });
        }
      }
    }
  }

  // LAY003 OVERLAP (error, reject): reuse the existing single-scope check.
  for (const err of validateNoOverlapsByScope(elements)) {
    findings.push({
      code: "LAY003",
      severity: "error",
      elementId: err.elementId,
      message: err.message,
    });
  }

  return findings;
}

/** Validate overlap only among elements that share the same parent and panel. */
export function validateNoOverlapsByScope(
  elements: LayElementInput[],
): ReturnType<typeof validateNoOverlaps>["errors"] {
  const scopes = new Map<string, LayElementInput[]>();
  for (const element of elements) {
    const scope = JSON.stringify([
      element.parentElementId ?? null,
      element.parentPanelId ?? null,
    ]);
    const scoped = scopes.get(scope) ?? [];
    scoped.push(element);
    scopes.set(scope, scoped);
  }

  return Array.from(scopes.values()).flatMap(
    (scoped) =>
      validateNoOverlaps(
        scoped.map((element) => ({
          id: element.id,
          gridColumn: element.gridColumn,
          gridRow: element.gridRow,
          columnSpan: Math.max(1, element.columnSpan),
          rowSpan: Math.max(1, element.rowSpan),
        })),
      ).errors,
  );
}

/** True if any finding blocks the mutation (spec: an `error` severity). */
export function hasBlockingFinding(findings: LayFinding[]): boolean {
  return findings.some((f) => f.severity === "error");
}
